/**
 * Service for handling serial communication with barcode scanners.
 * Utilizes the Web Serial API to establish the connection and read the data.
 */

declare global {
  interface Navigator {
    serial: {
      getPorts(): Promise<Array<SerialPort>>
      requestPort(options?: SerialPortRequestOptions): Promise<SerialPort>
    }
  }

  type SerialPortRequestOptions = {
    filters?: Array<SerialPortFilter>
  }

  type SerialPortFilter = {
    usbVendorId?: number
    usbProductId?: number
  }

  type SerialPort = {
    readable: ReadableStream<Uint8Array> | null
    writable: WritableStream<Uint8Array> | null
    open(options: SerialOptions): Promise<void>
    close(): Promise<void>
    getInfo(): SerialPortInfo
  }

  type SerialOptions = {
    baudRate: number
    dataBits?: number
    stopBits?: number
    parity?: 'none' | 'even' | 'odd'
    bufferSize?: number
    flowControl?: 'none' | 'hardware'
  }

  type SerialPortInfo = {
    usbVendorId?: number
    usbProductId?: number
  }
}

type ScannerCallback = (barcode: string) => void

class SerialScannerService {
  private static instance: SerialScannerService | null = null

  /**
   * Returns the singleton instance of SerialScannerService
   */
  public static getInstance(): SerialScannerService {
    if (!SerialScannerService.instance) {
      SerialScannerService.instance = new SerialScannerService()
    }
    return SerialScannerService.instance
  }

  private port: SerialPort | null = null
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null
  private isReading = false
  private buffer = ''
  private decoder = new TextDecoder()
  private onBarcodeScanned: ScannerCallback | null = null
  private isConnecting = false

  public isSupported = (): boolean => 'serial' in navigator

  public isSerialConfigured = (): boolean => localStorage.getItem('scanner_enabled') === 'true'

  public isConnected = (): boolean => {
    return !!(this.port && this.port.readable)
  }

  public setOnBarcodeScanned = (callback: ScannerCallback | null): void => {
    this.onBarcodeScanned = callback
  }

  public connect = async (): Promise<boolean> => {
    if (this.port && this.port.readable) {
      return true
    }

    if (this.isConnecting || !this.isSupported() || !this.isSerialConfigured()) {
      return false
    }

    this.isConnecting = true

    // Reset any existing port that might be in an invalid state
    if (this.port) {
      this.port = null
    }

    try {
      const ports = await navigator.serial.getPorts()
      if (ports.length === 0) {
        this.isConnecting = false
        return false
      }

      const savedPortInfoString = localStorage.getItem('scanner_serial_port_info')
      let selectedPort: SerialPort | undefined

      if (savedPortInfoString) {
        const savedPortInfo = JSON.parse(savedPortInfoString)

        if ('usbVendorId' in savedPortInfo && 'usbProductId' in savedPortInfo) {
          selectedPort = ports.find(port => {
            const info = port.getInfo()
            return (
              info.usbVendorId === savedPortInfo.usbVendorId &&
              info.usbProductId === savedPortInfo.usbProductId
            )
          })
        }
      }

      if (!selectedPort) {
        this.isConnecting = false
        this.disconnect()
        return false
      }

      if (this.port === selectedPort) {
        return true
      }

      this.port = selectedPort

      const baudRate = parseInt(
        localStorage.getItem('scanner_baud_rate') || '9600'
      )

      await this.port.open({ baudRate })

      this.isReading = true
      this.startReadingLoop()
      this.isConnecting = false
      return true
    } catch (error) {
      // TODO: Clean console logs errors once we have a stable version
      // eslint-disable-next-line no-console
      console.error('Error in connect method or during port open:', error)

      if (this.port) {
        try {
          await this.port.close().catch(err => {
            // eslint-disable-next-line no-console
            console.warn('Error closing port after failure:', err)
          })
        } catch (closeError) {
          // eslint-disable-next-line no-console
          console.error('Error trying to close port after a connect error:', closeError)
        }
      }
      this.port = null
      this.isConnecting = false
      return false
    }
  }

  public disconnect = async (): Promise<void> => {
    this.isReading = false
    this.isConnecting = false

    if (this.reader) {
      try {
        await this.reader.cancel()
        this.reader.releaseLock()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error cancelling reader:', error)
      }
      this.reader = null
    }

    if (this.port) {
      try {
        // Check if port is readable before trying to close, indicates it might be open
        if (this.port.readable) {
          await this.port.close()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error closing port during disconnect:', error)
      }
      this.port = null
    }
  }

  private startReadingLoop = async (): Promise<void> => {
    if (!this.port?.readable) {
      return
    }

    this.reader = this.port.readable.getReader()

    try {
      while (this.isReading) {
        const { value, done } = await this.reader.read()
        if (done) {
          break
        }
        if (value) {
          this.handleData(value)
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  private handleData = (value: Uint8Array): void => {
    const chunk = this.decoder.decode(value, { stream: true })
    this.buffer += chunk

    const lines = this.buffer.split(/[\r\n\t\0]+/)

    if (!/[\r\n\t\0]$/.test(this.buffer)) {
      this.buffer = lines.pop() || ''
    } else {
      this.buffer = ''
    }

    const validLines = lines.filter(line => line.trim().length > 0)

    validLines.forEach(line => {
      if (line && this.onBarcodeScanned) {
        this.onBarcodeScanned(line.trim())
      }
    })
  }
}

// Export the singleton instance
export const serialScannerService = SerialScannerService.getInstance()

